import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.js';
import { asStore } from '@src/application/store/as-store.js';
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { v4 } from 'uuid';
import showSideBarStore from '@src/application/store/show-side-bar-store.ts';

export function useStickyWrapperComposable() {

    /**
     * Jeder Sticky-Wrapper erzeugt eine uuid, um sich in den verticalPositionStore zu schreiben
     *
     * Bsp: d3965e70-6dab-4096-9958-4ef5c6066441
     */
    const uuid = v4();

    /**
     * topRef ist der Marker des Sticky-Wrappers. Der Marker ist ein Hilfselement, zur Steuerung der Positionierung zwischen 'relativ' und 'fixed'.
     */
    const { setSingleTemplateRef: setTopRef, singleRef: topRef } = useSingleRefComposable<Element>();

    /**
     * die stickyRef ist das div innerhalb des Sticky-Wrappers, welches auf 'relativ'/'fixed' gesetzt wird und die Elemente enthält, die sich sticky verhalten sollen.
     */
    const { setSingleTemplateRef: setStickyRef, singleRef: stickyRef } = useSingleRefComposable<Element>();

    /**
     * Die vertikale Position ist die aktuelle Position in der Reihenfolge aller gemounteten Sticky-Wrapper - also eine Zahl von 0 bis ... Anzahl der Sticky-Wrapper
     *
     * Bsp: 1 (siehe verticalPositionsStore)
     */
    const verticalPosition = ref<null|number>(null);

    /**
     * Der verticalPositionsStore speichert die y-Position in Pixeln zur uuid aller gemounteten Sticky-Wrapper
     *
     * Bsp: {
     *          'd3965e70-6dab-4096-9958-4ef5c6066441': 100, <-- aktueller StickyWrapper (s. uuid
     *          '90cd2237-0363-4788-9554-35632fbf4367': 200,
     *          'ef1fb0e8-3824-4218-8afd-f8291753b4ac': 0,
     *      }
     */
    const verticalPositionsStore = asStore('verticalPositionsStore', () => reactive<{ [uuid: string]: number }>({}));

    /**
     * Die vertikale Position wird in den Store geschrieben und alle Werte aufsteigend sortiert.
     * Funktion wird initial onMounted und on scroll ausgeführt.
     * Die Funktion onscroll auszuführen wird benötigt, um beim Seitenwechsel innerhalb der Applikation die Sticky-Wrapper,
     * die nicht neu gemountet werden mit ihrer neuen Position in der Store zuschreiben und richtig einzuordnen.
     *
     * Bsp:
     * verticalPositionsStore: {
     *          'd3965e70-6dab-4096-9958-4ef5c6066441': 100, <-- aktueller StickyWrapper (s. uuid
     *          '90cd2237-0363-4788-9554-35632fbf4367': 200,
     *          'ef1fb0e8-3824-4218-8afd-f8291753b4ac': 0,
     *      }
     * verticalPositions: [0, 100, 200]
     * verticalPosition: 1 (index der 100)
     */
    function calculateVerticalPosition() {
        if (topRef.value === null) {
            return;
        }
        verticalPositionsStore[uuid] = topRef.value.getBoundingClientRect().y;
        const verticalPositions = [...new Set(Object.values(verticalPositionsStore))].sort((a, b) => a - b);
        verticalPosition.value = verticalPositions.indexOf(verticalPositionsStore[uuid]);
    }
    onMounted(calculateVerticalPosition);
    window.addEventListener('scroll', calculateVerticalPosition);

    /**
     * #robustness
     * Weitere StickyWrapper leeren zunächst immer den VerticalPositionsStore, dieser wird dann über den unten stehenden watcher neu befüllt
     */
    onMounted(() => {
        for (const uuid in verticalPositionsStore) {
            delete verticalPositionsStore[uuid];
        }
    });
    watch(verticalPositionsStore, calculateVerticalPosition, { deep: true });

    onUnmounted(() => {
        if (verticalPosition.value === null) {
            return;
        }
        delete verticalPositionsStore[uuid];
    });

    /**
     * Der heightsStore speichert die Höhen aller Sticky-Wrapper in der Reihenfolge, in der sie untereinander angeordnet werden sollen.
     *
     * Bsp: {
     *         0: 20,
     *         2: 20,
     *         1: 130, <-- aktueller Sticky-Wrapper (s. verticalPosition)
     *      }
     */
    const heightsStore = asStore('heightsStore', () => reactive<{ [sequence: number]: number }>({}));
    watch(verticalPosition, () => {
        if (stickyRef.value === null) {
            return;
        }
        if (verticalPosition.value === null) {
            return;
        }

        /**
         * An dieser Stelle wird der heightsStore zur aktuellen verticalPosition befüllt. Zwei Sticky-Wrapper mit derselben position auf der y-Achse können NICHT verarbeitet werden, weil die Höhe für die gleiche Position überschrieben wird.
         */
        heightsStore[verticalPosition.value] = stickyRef.value?.getBoundingClientRect().height;
    }, { immediate: true });

    /**
     * Die height-Ref wird benötigt, um die Größe der im Sticky-Wrapper enthaltenen Elemente an das äußerste div der Komponente zu schreiben.
     * Anderenfalls verliert das äußerste div seine Höhe, wenn sein Inhalt 'fixed' wird. Das kann dazu führen, das die Seite "hüpft" (Beispiel: Sidebar Rekla)
     *
     * Außerdem wird die Höhe benötigt, um das root-margin zu berechnen.
     *
     * Bsp: 130
     */
    const height = ref(0);

    /**
     * Der resizeObserverHeight schreibt die aktuelle Höhe in den heightsStore und in die height Ref.
     * Die Höhe wird für die Berechnung des root-margins und des Abstands zum oberen Bildschirmrand benötigt.
     * https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
     */
    const resizeObserverHeight = new ResizeObserver((entries) => {
        if (verticalPosition.value === null) {
            return;
        }
        height.value = entries[0].contentRect.height;
        heightsStore[verticalPosition.value] = height.value;
    });
    onMounted(() => {
        if (stickyRef.value === null) {
            throw new Error('stickyRef not found');
        }
        resizeObserverHeight.observe(stickyRef.value);
    });
    onBeforeUnmount(() => {
        if (stickyRef.value === null) {
            throw new Error('stickyRef not found');
        }
        resizeObserverHeight.unobserve(stickyRef.value);
    });
    onUnmounted(() => {
        if (verticalPosition.value === null) {
            return;
        }
        delete heightsStore[verticalPosition.value];
    });

    /**
     * Setzt den Abstand des Sticky-Wrappers zum oberen Bildschirmrand auf den Wert des root-margins.
     * Somit positionieren sich alle aufeinanderfolgenden Sticky-Wrapper auf der Seite untereinander und haben entsprechend ihrer Vorgänger Abstand zum oberen Bildschirmrand.
     */
    const marginTop = computed(() => {
        if (position.value === 'relative') {
            return 0;
        }
        return rootMargin.value;
    });

    /**
     * Das root-margin wird anhand der Summe der Höhen der vorangegangenen Elemente ausgerechnet.
     * Wenn das root-margin mit >= 0 im IntersectionObserver angegeben wird, wird dem root-Element dieser Wert als
     * Versatz zum oberen Rand der Seite hinzugefügt und verschiebt den Triggerpunkt unter den vorherigen Sticky-Wrapper.
     * Aus diesem Grund wird beim scrollen der aktuelle Sticky-Wrapper erst wieder 'relativ', wenn er unter dem
     * verschobenen Triggerpunkt hervorgescrollt wird nicht schon bei Wiedererreichen des Viewports.
     * https://developer.mozilla.org/de/docs/Web/API/IntersectionObserver/rootMargin
     */
    const rootMargin = computed(() => {
        let margin = 0;
        if (verticalPosition.value !== null) {
            for (const storePosition in heightsStore) {
                if (Number.parseInt(storePosition) >= verticalPosition.value) {
                    continue;
                }
                margin += heightsStore[storePosition];
            }
        }
        return margin;
    });

    /**
     * Die position steuert die CSS-Eigenschaft 'position' des Sticky-Wrappers, der von 'relative' zu 'fixed' wechselt, wenn die topRef (Marker) am root-margin des topRefRelativeIntersectionObserver anschlägt.
     */
    const position = ref<'relative'|'fixed'>('relative');
    function topRefIntersectionCallback(entries: Array<IntersectionObserverEntry>) {
        calculateVerticalPosition();
        if (topRef.value === null) {
            return;
        }
        const entry = entries[0];
        if (!entry.isIntersecting) {
            position.value = 'fixed';
            return;
        }
        position.value = 'relative';
    }

    /**
     * topRefRelativeIntersectionObserver überwacht die topRef (Marker) des Sticky-Wrappers, berücksichtigt das
     * berechnete root-margin und steuert das Positionierungsverhalten (Wechsel von 'fixed' zu 'relative').
     * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
     */
    let topRefRelativeIntersectionObserver = new IntersectionObserver(topRefIntersectionCallback);
    onMounted(() => {
        if (topRef.value === null) {
            throw new Error('topRef not found');
        }
        topRefRelativeIntersectionObserver.observe(topRef.value);
    });
    watch(rootMargin, () => {
        if (topRef.value === null) {
            return;
        }
        topRefRelativeIntersectionObserver.disconnect();
        topRefRelativeIntersectionObserver = new IntersectionObserver(topRefIntersectionCallback, { rootMargin: rootMargin.value * -1 + 'px 0px 0px 0px' });
        topRefRelativeIntersectionObserver.observe(topRef.value);
    }, { immediate: true });

    /**
     * Die width wird benötigt, da der Sticky-Wrapper im 'fixed' Zustand sonst seine Größe verliert (rutscht zusammen auf die benötigte Breite der innenliegenden Elemente).
     */
    const width = ref('initial');

    /**
     * Da Elemente dynamisch wachsen oder schrumpfen können überwacht der resizeObserverWidth die Breite und schreibt sie in die width-Ref.
     */
    const resizeObserverWidth = new ResizeObserver((entries) => {
        if (!entries[0].target.checkVisibility()) {
            return;
        }
        width.value = entries[0].contentRect.width + 'px';
    });
    onMounted(() => {
        if (stickyRef.value === null) {
            throw new Error('stickyRef not found');
        }
        resizeObserverWidth.observe(stickyRef.value);
    });
    onBeforeUnmount(() => {
        if (stickyRef.value === null) {
            throw new Error('stickyRef not found');
        }
        resizeObserverWidth.unobserve(stickyRef.value);
    });

    /**
     * Elemente wachsen und schrumpfen ebenfalls, wenn sich die Größe des Browserfensters ändert. Deshalb schreiben wir auch dann die Breite des Sticky-Wrappers in die Ref.
     */
    function resizeByTopRef() {
        if (topRef.value === null) {
            throw new Error('topRef not found');
        }
        width.value = topRef.value.clientWidth + 'px';
    }
    window.addEventListener('resize', resizeByTopRef);
    onUnmounted(() => window.removeEventListener('resize', resizeByTopRef));

    /**
     * Wenn das Menu ein- oder ausgeklappt wird, dann müssen wir die Breite anpassen.
     */
    watch(() => showSideBarStore.showSideBar.data, async () => {
        await nextTick();
        resizeByTopRef();
    });

    return {
        setTopRef,
        setStickyRef,
        height,
        marginTop,
        position,
        width,
    };
}
